import { validIcons } from '@brahma/components';
import type { DashboardNavItem, Sublink } from '@dfg/brahma-types';
import type { TFunction } from 'i18next';
import { DEFAULT_LNG } from '$locale/constants';

export const getDashboardNavItem = (
  item: DashboardNavItem,
  t: TFunction,
  isMobile?: boolean
): DashboardNavItem | null => {
  switch (item.id) {
    case 'home':
      return {
        id: '/dashboard/home',
        tabName: t('layout.home'),
        activeIconName: validIcons.home02Custom,
        passiveIconName: validIcons.passiveHome
      };
    case 'verification':
      return {
        id: '/dashboard/verification',
        tabName: item.tabName, // Use backend translation
        activeIconName: validIcons.checkVerified02Custom,
        passiveIconName: validIcons.passiveCheckVerified,
        sublinks: item?.sublinks
          ? item.sublinks.map((sublink: Sublink) => {
              return {
                id: `${sublink.id}`,
                tabName: sublink.tabName // Use backend translation directly
              };
            })
          : [],
        isNonLink: !isMobile
      };
    case 'services':
      return {
        id: '/dashboard/services',
        tabName: item.tabName, // Use backend translation
        activeIconName: validIcons.grid01Custom,
        passiveIconName: validIcons.passiveGrid,
        sublinks: item?.sublinks
          ? item.sublinks.map((sublink: Sublink) => ({
              id: `/dashboard/services/${sublink.id}`,
              tabName: sublink.tabName
            }))
          : [],
        isNonLink: false
      };
    case 'trueProfileWallet':
      return {
        id: '/dashboard/vault',
        tabName: item.tabName, // Use backend translation
        activeIconName: validIcons.TPIcon,
        passiveIconName: validIcons.TPIcon
      };
    case 'settings':
      return {
        id: '/dashboard/manage-account',
        tabName: t('layout.settings'),
        activeIconName: validIcons.setting01Custom,
        passiveIconName: validIcons.passiveSetting
      };
    case 'chatbot':
      return isMobile
        ? {
            id: 'chatbot',
            tabName: item.tabName,
            activeIconName: validIcons.messageDotsCircle,
            passiveIconName: validIcons.messageDotsCircle,
            isNonLink: true
          }
        : null;
    case 'support':
      return {
        id: 'support',
        tabName: item.tabName, // Use backend translation
        activeIconName: validIcons.messageChatPrimary,
        passiveIconName: validIcons.messageChatNormal,
        isNonLink: true
      };
    default:
      if (item?.tabName === 'Job Verification' && isMobile) {
        return null;
      }
      return {
        ...item,
        activeIconName: validIcons[item.activeIconName],
        passiveIconName: validIcons[item.passiveIconName]
      };
  }
};

export const getDefaultDashboardNavItems = (
  t: TFunction,
  filterFn?: (item: DashboardNavItem) => boolean
): DashboardNavItem[] => {
  const allItems: DashboardNavItem[] = [
    {
      id: '/dashboard/home',
      tabName: t('layout.home'),
      activeIconName: validIcons.home02Custom,
      passiveIconName: validIcons.passiveHome,
      showUnauthenticated: true
    },
    {
      id: '/dashboard/verification',
      tabName: t('layout.verification'),
      activeIconName: validIcons.checkVerified02Custom,
      passiveIconName: validIcons.passiveCheckVerified,
      showUnauthenticated: false
    },
    {
      id: '/dashboard/vault',
      tabName: t('breadCrumbs.TruLocker'),
      activeIconName: validIcons.TPIcon,
      passiveIconName: validIcons.TPIcon,
      showUnauthenticated: false
    },
    {
      id: '/dashboard/manage-account',
      tabName: t('layout.settings'),
      activeIconName: validIcons.setting01Custom,
      passiveIconName: validIcons.passiveSetting,
      showUnauthenticated: false
    },
    {
      id: '/dashboard/services',
      tabName: t('layout.services'),
      activeIconName: validIcons.grid01Custom,
      passiveIconName: validIcons.passiveGrid,
      showUnauthenticated: false
    },
    {
      id: 'support',
      tabName: t('layout.support'),
      activeIconName: validIcons.messageChatPrimary,
      passiveIconName: validIcons.messageChatNormal,
      showUnauthenticated: false
    }
  ];

  // Apply the filter function if provided; otherwise, return all items
  return filterFn ? allItems.filter(filterFn) : allItems;
};

// extensible nav item filter
export const createNavItemFilter = (
  isAuthenticated: boolean
): ((item: DashboardNavItem) => boolean) | undefined => {
  if (!isAuthenticated) {
    return (item: DashboardNavItem) => item.showUnauthenticated === true;
  }

  // Add more conditions here as needed for future cases
  return undefined;
};

export const getOnboardingWarningMessage = (
  msg: Record<string, string> | undefined,
  lang: string
): string => {
  if (!msg) return '';
  return msg[lang] || msg[DEFAULT_LNG] || '';
};
